<template>
  <sp-text-field
    ref="textField"
    class="finder-filter-field-activator"
    readonly
    :disabled="disabled"
    :clearable="clearable"
    :persistent-clear="clearable"
    :input-test-id="inputTestId"
    :value.prop="displayText"
    :aria-label="ariaLabel"
    propagate-append-click
    @keydown="handleActivatorKeydown"
    @click-clear="handleClear"
    @change.stop
  >
    <sp-icon
      v-if="prependIcon"
      slot="prepend"
      aria-hidden="true"
      class="finder-filter-field-activator__icon"
      :name="prependIcon"
    ></sp-icon>
    <sp-icon slot="append" class="finder-filter-field-activator__caret" :name="appendIcon"></sp-icon>
  </sp-text-field>
</template>

<script setup>
import { computed, onMounted, shallowRef, useTemplateRef } from "vue";
import { useEventListener } from "@vueuse/core";

import { useAriaLabel } from "#ce/composables/useAriaLabel";
import { MOUNT_READY_EVENT } from "#ce/framework/mount-ready";
import { booleanAttribute } from "#ce/utils/props";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  clearable: booleanAttribute(),
  inputTestId: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    required: true,
  },
  text: {
    type: String,
    default: undefined,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  prependIcon: {
    type: String,
    default: undefined,
  },
  appendIcon: {
    type: String,
    default: "popup-arrow-filled",
  },
  disabled: booleanAttribute(),
  ariaLabel: {
    type: String,
    default: undefined,
  },
  ariaLabelledby: {
    type: String,
    default: undefined,
  },
});

const displayText = computed(() => props.text ?? props.placeholder ?? props.label);

const emit = defineEmits(["clear"]);

function handleClear() {
  emit("clear");
}

defineExpose({ label: computed(() => props.label) });

const textField = useTemplateRef("textField");
const input = shallowRef(null);
const { ariaLabel } = useAriaLabel(input, {
  label: computed(() => props.ariaLabel ?? props.label),
  labelledby: computed(() => props.ariaLabelledby),
});

function handleFieldReady() {
  input.value = textField.value?.inputElement ?? null;
}

onMounted(handleFieldReady);
useEventListener(textField, MOUNT_READY_EVENT, handleFieldReady);

function handleActivatorKeydown(event) {
  if (props.disabled || (event.key !== "Enter" && event.key !== " ")) {
    return;
  }

  if (event.composedPath().some((node) => node.classList?.contains("sp-text-field-clear"))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.currentTarget.click();
}
</script>

<style scoped lang="scss">
@import "@assets/mixins/breakpoint";

.finder-filter-field-activator {
  --sp-comp-text-field-min-height: var(--sp-comp-finder-filter-field-height, var(--sp-sys-form-field-min-height));
  --sp-comp-text-field-compact-min-height: var(
    --sp-comp-finder-filter-field-height,
    var(--sp-sys-form-field-min-height)
  );
  --sp-comp-text-field-cursor: pointer;
  --sp-comp-text-field-content-padding-inline: 0;

  @include sp-breakpoint(xs) {
    --sp-comp-text-field-font: var(
      --sp-comp-finder-filter-field-mobile-control-font-family,
      var(--sp-sys-font-family-normal)
    );
    --sp-comp-text-field-font-size: var(
      --sp-comp-finder-filter-field-mobile-control-font-size,
      var(--sp-sys-form-field-font-size)
    );
    --sp-comp-text-field-border-color: var(
      --sp-comp-finder-filter-field-mobile-control-border-color,
      var(--sp-sys-form-field-border-color)
    );
    --sp-comp-text-field-border-radius: var(
      --sp-comp-finder-filter-field-mobile-control-border-radius,
      var(--sp-sys-shape-control)
    );
    --sp-comp-text-field-padding-inline: var(
      --sp-comp-finder-filter-field-mobile-control-padding-inline,
      var(--sp-sys-form-field-padding-inline)
    );
  }

  &__icon {
    --fill: var(--sp-sys-color-primary);
  }

  @include sp-breakpoint(sm) {
    --sp-comp-text-field-padding-block: var(--sp-comp-finder-filter-field-control-padding-block, 0);
    --sp-comp-text-field-font: var(--sp-comp-finder-filter-field-control-font-family, var(--sp-sys-font-family-normal));
    --sp-comp-text-field-font-size: var(
      --sp-comp-finder-filter-field-control-font-size,
      var(--sp-sys-form-field-font-size)
    );
    --sp-comp-text-field-color: var(--sp-comp-finder-filter-field-control-color, var(--sp-sys-color-on-surface));
    --sp-comp-text-field-background-color: var(
      --sp-comp-finder-filter-field-control-background,
      var(--sp-sys-color-surface)
    );
    --sp-comp-text-field-border-color: var(
      --sp-comp-finder-filter-field-control-border-color,
      var(--sp-sys-form-field-border-color)
    );
    --sp-comp-text-field-border-radius: var(
      --sp-comp-finder-filter-field-control-border-radius,
      var(--sp-sys-shape-control)
    );
    --sp-comp-text-field-padding-inline: var(
      --sp-comp-finder-filter-field-control-padding-inline,
      var(--sp-ref-scale-10)
    );

    .finder-filter-field-activator__icon {
      display: none;
    }

    .finder-filter-field-activator__caret {
      --fill: var(--sp-comp-finder-filter-field-control-color, var(--sp-sys-color-on-surface));
      --sp-comp-icon-size: var(--sp-comp-finder-filter-field-caret-size, var(--sp-ref-scale-12));

      margin-inline-end: var(--sp-comp-finder-filter-field-control-padding-inline, var(--sp-ref-scale-10));
      display: var(--sp-comp-finder-filter-field-caret-display, flex);
      justify-content: center;
      height: var(--sp-comp-finder-filter-field-caret-height, auto);
      font: var(--sp-comp-finder-filter-field-caret-font, inherit);
      text-align: center;
      width: var(--sp-comp-finder-filter-field-caret-width, var(--sp-ref-scale-32));

      &::after {
        content: var(--sp-comp-finder-filter-field-caret-content, none);
        font: var(--sp-comp-finder-filter-field-caret-glyph-font, inherit);
        vertical-align: middle;
      }
    }
  }
}
</style>
